/**
 * Hook para gerenciamento de Itens (Produtos/Serviços) de Deals CRM
 * Fornece queries e mutations para produtos dentro de um negócio
 *
 * Versão: 2.0.0
 * - Refatoração 100% server-side: substitui SELECT/INSERT/UPDATE/DELETE
 *   diretos em crm_deal_items pelas RPCs:
 *     get_deal_items_full / add_deal_item / update_deal_item / delete_deal_item
 * - Sincronização do valor do deal já é feita dentro das próprias RPCs
 *   (sync_deal_value_from_items é chamado server-side).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useCallback } from 'react';
import { WORKFLOW_DEAL_CHANGED_EVENT } from '@/hooks/useWorkflowInstanceTimeline';

// Logger para o módulo de Itens de Deals
const logger = createLogger('DealItems');

// Interface do Item do Deal
export interface DealItem {
  id: string;
  tenant_id: string;
  deal_id: string;
  product_id: string;
  price_table_id: string | null;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  discount_fixed: number;
  total: number;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Relacionamento carregado
  product?: {
    id: string;
    name: string;
    sku: string | null;
    product_type: 'product' | 'service';
    unit_type: string;
    default_price: number;
    // Campos de serviço recorrente (v1.1.0 do get_deal_items_full)
    recurring_price?: number | null;
    recurrence_interval?: import('@/lib/crm/recurringServiceUtils').RecurrenceInterval | null;
    term_type?: import('@/lib/crm/recurringServiceUtils').TermType | null;
    term_quantity?: number | null;
    term_unit?: import('@/lib/crm/recurringServiceUtils').TermUnit | null;
  } | null;
}

// Input para adicionar item
export interface AddDealItemInput {
  deal_id: string;
  product_id: string;
  quantity?: number;
  unit_price: number;
  discount_percent?: number;
  discount_fixed?: number;
  notes?: string;
  price_table_id?: string | null;
}

// Input para atualizar item
export interface UpdateDealItemInput {
  id: string;
  quantity?: number;
  unit_price?: number;
  discount_percent?: number;
  discount_fixed?: number;
  notes?: string | null;
}

// Opções de configuração do hook
export interface DealItemsOptions {
  enabled?: boolean;
}

/**
 * Hook para itens de um deal específico
 * Suporta lazy loading via options.enabled
 */
export function useCrmDealItems(dealId: string | null, options?: DealItemsOptions) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Determina se query deve executar (lazy loading)
  const isEnabled = (options?.enabled ?? true) && !!dealId && !!currentTenant?.id;

  /**
   * Função auxiliar para sincronizar valor do deal com total dos itens.
   * Mantida como fallback opcional. As RPCs add/update/delete já chamam
   * sync_deal_value_from_items internamente — esta função só é usada se
   * algum fluxo externo precisar forçar a sincronização.
   */
  const syncDealValue = useCallback(async (targetDealId: string) => {
    try {
      const { error } = await supabase.rpc('sync_deal_value_from_items', {
        p_deal_id: targetDealId,
      });
      if (error) {
        logger.warn('Erro ao sincronizar valor do deal:', error.message);
      }
    } catch (error) {
      logger.warn('Exceção ao sincronizar valor do deal:', error);
    }
  }, []);

  // Helper de invalidação de caches relacionados a itens do deal
  const invalidateDealCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['crm-deal-items', dealId] });
    queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
    queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['crm-deal', dealId] });
    queryClient.invalidateQueries({ queryKey: ['deal-modal-data', dealId] });
    queryClient.invalidateQueries({ queryKey: ['deal-items-for-charge', dealId] });
    // Trilha do processo (Workflow 2.0): a mudança de valor pode disparar
    // entrada/saída da esteira; invalida e abre a janela de acompanhamento
    queryClient.invalidateQueries({ queryKey: ['deal-workflow', dealId] });
    // Sinal leve que liga o refetch curto da trilha (~60s) — ver useWorkflowInstanceTimeline
    window.dispatchEvent(
      new CustomEvent(WORKFLOW_DEAL_CHANGED_EVENT, { detail: { rootEntityId: dealId } }),
    );
  }, [queryClient, dealId]);

  // Query para listar itens do deal — agora via RPC server-side
  const itemsQuery = useQuery({
    queryKey: ['crm-deal-items', dealId],
    queryFn: async () => {
      if (!dealId || !currentTenant?.id) return [];

      const { data, error } = await (supabase.rpc as any)('get_deal_items_full', {
        p_deal_id: dealId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar itens do deal:', error.message);
        throw error;
      }
      return (data || []) as DealItem[];
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });

  // Mutation para adicionar item — RPC server-side (resolve sort_order e sincroniza valor)
  const addItem = useMutation({
    mutationFn: async (input: AddDealItemInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      const { data, error } = await (supabase.rpc as any)('add_deal_item', {
        p_deal_id: input.deal_id,
        p_product_id: input.product_id,
        p_unit_price: input.unit_price,
        p_quantity: input.quantity ?? 1,
        p_discount_percent: input.discount_percent ?? 0,
        p_discount_fixed: input.discount_fixed ?? 0,
        p_notes: input.notes ?? null,
        p_price_table_id: input.price_table_id ?? null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao adicionar item:', error.message);
        throw error;
      }
      // RPC retorna SETOF — pegamos o primeiro registro
      return (Array.isArray(data) ? data[0] : data) as DealItem;
    },
    onSuccess: () => {
      invalidateDealCaches();
      toast.success('Produto adicionado ao negócio!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao adicionar produto: ${error.message}`);
    },
  });

  // Mutation para atualizar item — RPC server-side
  const updateItem = useMutation({
    mutationFn: async (input: UpdateDealItemInput) => {
      const { data, error } = await (supabase.rpc as any)('update_deal_item', {
        p_item_id: input.id,
        p_quantity: input.quantity ?? null,
        p_unit_price: input.unit_price ?? null,
        p_discount_percent: input.discount_percent ?? null,
        p_discount_fixed: input.discount_fixed ?? null,
        // notes: null explícito significa "limpar"; undefined significa "manter"
        p_notes: input.notes === undefined ? null : input.notes,
        p_clear_notes: input.notes === null,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('Erro ao atualizar item:', error.message);
        throw error;
      }
      return (Array.isArray(data) ? data[0] : data) as DealItem;
    },
    onSuccess: () => {
      invalidateDealCaches();
      // Sem toast para edições inline — feedback visual já é suficiente
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar item: ${error.message}`);
    },
  });

  // Mutation para remover item — RPC server-side
  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await (supabase.rpc as any)('delete_deal_item', {
        p_item_id: itemId,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('Erro ao remover item:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateDealCaches();
      toast.success('Produto removido do negócio!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover produto: ${error.message}`);
    },
  });

  // Calcula totais
  const totals = {
    subtotal: itemsQuery.data?.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) || 0,
    totalDiscounts: itemsQuery.data?.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const percentDiscount = itemSubtotal * (item.discount_percent / 100);
      return sum + percentDiscount + item.discount_fixed;
    }, 0) || 0,
    total: itemsQuery.data?.reduce((sum, item) => sum + item.total, 0) || 0,
  };

  return {
    items: itemsQuery.data || [],
    isLoading: itemsQuery.isLoading,
    error: itemsQuery.error,
    addItem,
    updateItem,
    removeItem,
    totals,
    refetch: itemsQuery.refetch,
  };
}
